import { useGetUserPodcasts } from "@packages/api/madlenAPI";
import PodcastAudioCard from "../../components/PodcastAudioCard";
import logo from "@packages/assets/logo.png";
import { useTranslation } from "react-i18next";
import CreatePodcastCard from "../../components/CreatePodcastCard";
import Joyride, { CallBackProps, Step } from "react-joyride";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useLocalStorage } from "@packages/hooks/useLocalStorage";
import { useMobile } from "@packages/hooks/useMobile";
import { joyrideStyles, joyrideStylesDark } from "@packages/utils";
import ErrorComponent from "../../../tools/components/tool_components/ErrorComponent";
import { Text } from "@core/text";
import { CreatePodcastFromReferenceRequestDtoGenerationType } from "@packages/api/madlenAPI.schemas";
import PodcastRegenModal from "@features/media-academy/components/PodcastRegenModal";
import HeroTitle from "@features/tools/components/tool_components/HeroTitle";
import Pagination from "@core/pagination";
import PodcastAssignmentModal from "@features/media-academy/components/PodcastAssignmentModal";
import PdfCustomizationModal from "@features/common/components/pdf-customization-modal";
import PodcastPdf from "@features/media-academy/components/PodcastPdf";
import { useAppSelector } from "@packages/store/hooks";
import ToolHelper from "@features/tools/components/ToolHelper";
import { useTheme } from "@packages/hooks/useTheme";
import { PodcastPlayerProvider } from "../../context/PodcastPlayerContext";

const AllPodcastsView = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [runTour, setRunTour] = useState(false);
  const [skipAllTours] = useLocalStorage("skip-all-tours", false);
  const isMobile = useMobile();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [regeneratePodcastId, setRegeneratePodcastId] = useState<
    number | undefined
  >(undefined);
  const [assignPodcastInfo, setAssignPodcastInfo] = useState<
    | {
        podcastId: number;
        podcastTitle: string;
      }
    | undefined
  >(undefined);
  const [pdfCustomizationId, setPdfCustomizationId] = useState<
    number | undefined
  >(undefined);
  const [regeneratePodcastGenerationType, setRegeneratePodcastGenerationType] =
    useState<CreatePodcastFromReferenceRequestDtoGenerationType | undefined>(
      undefined,
    );

  // Global state for currently playing podcast
  const [currentlyPlayingId, setCurrentlyPlayingId] = useState<number | null>(
    null,
  );

  const customizationSettings = useAppSelector(
    (state) => state.user.teacher?.preferences?.pdfOptions,
  );

  const {
    data: podcastsData,
    isLoading,
    error,
    refetch,
  } = useGetUserPodcasts({
    page: currentPage,
    size: pageSize,
  });

  const steps: Step[] = [
    {
      target: "body",
      content: t("onboarding.podcast.tour.welcome"),
      placement: "center",
      disableBeacon: true,
    },
    {
      target: ".generate-podcast-form",
      content: t("onboarding.podcast.tour.form"),
      placement: "right-start",
    },
    {
      target: ".generate-podcast-button",
      content: t("onboarding.podcast.tour.explore"),
      placement: "bottom",
    },
  ];

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status, action } = data;

    if (action === "close") {
      setRunTour(false);
      return;
    }

    if (status === "finished" || status === "skipped") {
      setRunTour(false);
    }
  };

  // Memoized callbacks to prevent unnecessary re-renders
  const onRegenClick = useCallback(
    (
      podcastId: number | undefined,
      podcastGenerationType: CreatePodcastFromReferenceRequestDtoGenerationType,
    ) => {
      setRegeneratePodcastId(podcastId);
      setRegeneratePodcastGenerationType(podcastGenerationType);
    },
    [],
  );

  const onAssignClick = useCallback(
    (podcastId: number | undefined, podcastTitle: string) => {
      if (podcastId) {
        setAssignPodcastInfo({ podcastId, podcastTitle });
      }
    },
    [],
  );

  const openPdfCustomizationModal = useCallback(
    (podcastId: number | undefined) => {
      setPdfCustomizationId(podcastId);
    },
    [],
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (
        newPage > 0 &&
        (!podcastsData?.totalPages || newPage <= podcastsData?.totalPages)
      ) {
        setCurrentPage(newPage);
        // Scroll to top below the create modal when changing pages
        window.scrollTo({ top: 450, behavior: "smooth" });
      }
    },
    [podcastsData?.totalPages],
  );

  // Memoized computed values
  const shouldShowPagination = useMemo(
    () =>
      !error &&
      podcastsData?.totalPages &&
      podcastsData?.totalPages > 1 &&
      !isLoading,
    [error, podcastsData?.totalPages, isLoading],
  );

  const selectedPodcastForPdf = useMemo(() => {
    return podcastsData?.data?.find(
      (podcast) => podcast.id === pdfCustomizationId,
    );
  }, [podcastsData?.data, pdfCustomizationId]);

  useEffect(() => {
    const shouldShowTour =
      !isLoading &&
      (!podcastsData?.data || podcastsData.data.length === 0) &&
      !skipAllTours &&
      !isMobile;
    setRunTour(shouldShowTour);
  }, [isLoading, podcastsData?.data, skipAllTours, isMobile]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-500"></div>
        <img
          src={logo}
          alt="logo"
          className="absolute w-8 h-8 animate-bounce mt-2"
        />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorComponent
        title={t("podcast.error.title")}
        refetch={refetch}
        description={t("podcast.error.description")}
        retryButton={t("podcast.error.retryButton")}
      />
    );
  }

  return (
    <PodcastPlayerProvider>
      <div className="min-h-screen px-4 py-4 bg-gradient-to-br from-gray-100 to-purple-100 dark:from-gray-900 dark:to-purple-900">
        <Joyride
          locale={{
            back: t("onboarding.back"),
            close: t("onboarding.close"),
            last: t("onboarding.finish"),
            next: t("onboarding.next"),
            skip: t("onboarding.skip"),
          }}
          steps={steps}
          run={!skipAllTours && !isMobile && runTour}
          continuous
          showSkipButton
          styles={theme === "dark" ? joyrideStylesDark : joyrideStyles}
          callback={handleJoyrideCallback}
        />

        {/* Hero Section */}
        <HeroTitle
          title={t("sidebar.menu.toolbox.podcasts")}
          description={t("podcast.description")}
          titleStyle="bg-gradient-to-r from-purple-600 via-pink-600 to-rose-600 dark:from-purple-400 dark:via-pink-400 dark:to-rose-400"
          titleBackgroundStyle="bg-gradient-to-r from-purple-50/30 to-pink-50/30 dark:from-purple-900/30 dark:to-pink-900/30"
        />

        <div className="px-4 py-4 mx-auto container max-w-7xl">
          <CreatePodcastCard
            onRegenClick={onRegenClick}
            onAssignClick={onAssignClick}
            openPdfCustomizationModal={openPdfCustomizationModal}
          />
          <Text variant="h1" className="my-8">
            {t("podcast.allPodcasts")}
          </Text>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 podcast-grid">
            {podcastsData?.data?.map((podcast) => (
              <div key={podcast.id} className="w-full overflow-hidden">
                <PodcastAudioCard
                  id={podcast.id!}
                  url={podcast.url}
                  name={podcast.title}
                  author={podcast.authorName}
                  transcript={podcast.transcript as any}
                  createdAt={podcast.createdAt}
                  imageUrl={podcast.imageUrl}
                  videoUrl={podcast.videoUrl}
                  podcastGenerationType={
                    podcast.teacherDocuments?.length
                      ? CreatePodcastFromReferenceRequestDtoGenerationType.TEACHER_DOCUMENT
                      : CreatePodcastFromReferenceRequestDtoGenerationType.PROMPT
                  }
                  onRegenClick={onRegenClick}
                  openPdfCustomizationModal={openPdfCustomizationModal}
                  currentlyPlayingId={currentlyPlayingId}
                  onPlayStateChange={setCurrentlyPlayingId}
                />
              </div>
            ))}
          </div>

          {(!podcastsData?.data || podcastsData.data.length === 0) && (
            <div className="text-center text-gray-500 dark:text-gray-400 mt-8">
              {t("podcast.noLessonsAvailable")}
            </div>
          )}

          {/* Pagination Controls */}
          {!!shouldShowPagination && (
            <Pagination
              currentPage={currentPage}
              totalPages={Number(podcastsData?.totalPages || 1)}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </div>
      <ToolHelper
        toolPath={"podcast"}
        shouldShowTour={
          !skipAllTours &&
          !isMobile &&
          podcastsData?.data &&
          podcastsData?.data?.length > 0
        }
        toolType={t(`sidebar.menu.toolbox.podcasts`)}
      />
      <PodcastAssignmentModal
        show={!!assignPodcastInfo?.podcastId}
        onClose={() => {
          setAssignPodcastInfo(undefined);
        }}
        podcastId={Number(assignPodcastInfo?.podcastId)}
        podcastTitle={assignPodcastInfo?.podcastTitle}
      />
      <PodcastRegenModal
        show={!!regeneratePodcastId}
        onClose={() => {
          setRegeneratePodcastId(undefined);
          setRegeneratePodcastGenerationType(undefined);
        }}
        podcastId={regeneratePodcastId}
        podcastGenerationType={regeneratePodcastGenerationType}
      />
      {/* PDF Customization Modal */}
      {selectedPodcastForPdf && (
        <PdfCustomizationModal
          show={!!pdfCustomizationId}
          onClose={() => setPdfCustomizationId(undefined)}
          hideSchoolLogoOptions
          documentComponent={
            <PodcastPdf
              questions={selectedPodcastForPdf.questions as any}
              author={selectedPodcastForPdf.authorName || ""}
              customizationSettings={customizationSettings as any}
              name={
                (selectedPodcastForPdf as any).name ||
                selectedPodcastForPdf.title
              }
              transcript={selectedPodcastForPdf.transcript as any}
              imageUrl={selectedPodcastForPdf.imageUrl}
              createdAt={selectedPodcastForPdf.createdAt}
            />
          }
        />
      )}
    </PodcastPlayerProvider>
  );
};

export default AllPodcastsView;
